import { Platform } from 'react-native';
import { format, isSameDay, isSameYear, isToday } from 'date-fns';
import { rem } from 'design-system/values';
import { formatAmount } from 'utils/formatting';
import monotoneCubicInterpolation from '../components/Chart/utils/monotoneCubicInterpolation';
import { STOCK_RESAMPLE_RANGE, ENABLE_STOCK_RESAMPLE, PORTFOLIO_RESAMPLE_RANGE, ACTUAL_CANDLE_CHART_HEIGHT, } from '../constants';
import { daysShort, months } from '.';
const nonUndefinedFilter = (value) => value !== undefined;
export const prepareChartPathProviderData = (response, options) => {
    const dataseries = 'closePrices' in response
        ? response.closePrices
        : response[options?.key ?? 'equity'];
    const isAllSame = dataseries.every((value) => value === null || value === dataseries[0]); // nulls don’t count
    let yRange;
    const midIndex = Math.floor((dataseries.length - 1) / 2);
    if (isAllSame) {
        if (dataseries[0] === null) {
            // if the very first point is already null, then all points are null - return [-1, 1]
            yRange = [-1, 1];
        }
        else {
            yRange = [dataseries[0] - 1, dataseries[0] + 1];
        }
    }
    else if (dataseries[midIndex] === null) {
        // Checking to see if the 1D graph is half empty and shift the graph to make it less jumpy
        const onlyNumbers = dataseries.filter((num) => typeof num === 'number');
        const max = Math.max(...onlyNumbers, onlyNumbers[0] + 2);
        const min = Math.min(...onlyNumbers, onlyNumbers[0] - 2);
        yRange = [min, max];
    }
    const adjustedDataseries = dataseries.map((value) => (value === null ? undefined : value));
    let points;
    let nativePoints;
    switch (options?.for) {
        case 'stock':
            if (adjustedDataseries.length === 1) {
                if (adjustedDataseries[0] === undefined) {
                    adjustedDataseries[0] = 0;
                    adjustedDataseries.push(0);
                }
                else {
                    adjustedDataseries.push(adjustedDataseries[0]);
                }
            }
            nativePoints = adjustedDataseries.map((datapoint, index) => ({
                x: index,
                y: datapoint,
            }));
            if (Platform.OS === 'ios' || !ENABLE_STOCK_RESAMPLE) {
                points = nativePoints;
            }
            else {
                const firstNullIndex = adjustedDataseries.findIndex((value) => value === undefined);
                const nullsCount = adjustedDataseries.length - (firstNullIndex === -1 ? adjustedDataseries.length : firstNullIndex);
                const nullPoints = [];
                for (let i = 0; i < nullsCount; i += 1) {
                    nullPoints.push({
                        x: firstNullIndex + i,
                        y: undefined,
                    });
                }
                const nonNullDataseries = adjustedDataseries.filter(nonUndefinedFilter);
                if (nonNullDataseries.length === 1) {
                    nonNullDataseries.push(nonNullDataseries[0]);
                }
                points = monotoneCubicInterpolation({
                    data: nonNullDataseries.map((datapoint, index) => ({
                        x: index,
                        y: datapoint,
                    })),
                    range: STOCK_RESAMPLE_RANGE,
                    includeExtremes: true,
                    removePointsSurroundingExtremes: false,
                }).concat(nullPoints);
            }
            break;
        default: {
            const nonNullDataseries = adjustedDataseries.filter(nonUndefinedFilter); // can be of zero length
            const factor = nonNullDataseries.length > 0 ? Math.floor(PORTFOLIO_RESAMPLE_RANGE / nonNullDataseries.length) || 1 : 1;
            points = monotoneCubicInterpolation({
                data: nonNullDataseries.map((datapoint, index) => ({
                    x: index,
                    y: datapoint,
                })),
                range: nonNullDataseries.length * factor - 1, // -1 value is handled okay here (in case nonNullDataseries.length = 0)
                includeExtremes: true,
                removePointsSurroundingExtremes: false,
            });
            const correctLength = factor * (adjustedDataseries.length - 1);
            if (points.length > 0) {
                for (let xMultipliedByFactor = points[points.length - 1].x * factor + 1; xMultipliedByFactor <= correctLength; xMultipliedByFactor += 1) {
                    points.push({ x: xMultipliedByFactor / factor, y: undefined });
                }
            }
            nativePoints = adjustedDataseries.map((datapoint, index) => ({
                x: index,
                y: datapoint,
            }));
            break;
        }
    }
    return {
        points,
        nativePoints,
        smoothingStrategy: 'simple', // only simple is supported for now
        yRange,
    };
};
export const appendNullsToChartResponse = (response, n) => {
    if ('timestamps' in response) {
        const result = { ...response };
        result.timestamps = result.timestamps.concat(Array.from({ length: n }).map(() => null));
        result.closePrices = result.closePrices.concat(Array.from({ length: n }).map(() => null));
        return result;
    }
    const result = { ...response };
    result.timestamp = result.timestamp.concat(Array.from({ length: n }).map(() => null));
    result.equity = result.equity.concat(Array.from({ length: n }).map(() => null));
    result.profitLoss = result.profitLoss.concat(Array.from({ length: n }).map(() => null));
    result.profitLossPct = result.profitLossPct.concat(Array.from({ length: n }).map(() => null));
    return result;
};
export const getProfitLoss = (dataseries) => {
    let i = dataseries.length - 1;
    while (dataseries[i] === null && i > 0) {
        i -= 1;
    }
    const result = dataseries[i];
    return result ?? 0;
};
const getColorKey = (priceChange) => {
    if (priceChange) {
        if (priceChange < 0) {
            return 'negative';
        }
        if (priceChange > 0) {
            return 'positive';
        }
        return 'secondary';
    }
    return undefined;
};
export const formatDate = (d, includeTime, showToday = true) => {
    let h = d.getHours();
    if (h < 10) {
        h = `0${h}`;
    }
    let m = d.getMinutes();
    if (m < 10) {
        m = `0${m}`;
    }
    let D = daysShort[d.getDay()];
    const now = new Date();
    if (showToday &&
        now.getFullYear() === d.getFullYear() &&
        now.getMonth() === d.getMonth() &&
        now.getDate() === d.getDate()) {
        D = 'Today';
    }
    let result = `${D}, ${d.getDate()} ${months[d.getMonth()]}`;
    if (now.getFullYear() !== d.getFullYear()) {
        result += ` ${d.getFullYear()}`;
    }
    if (includeTime) {
        result = `${h}:${m} ${result}`;
    }
    return result;
};
const getFormattedTimeStamp = (timestamp, chartPeriod) => {
    if (timestamp) {
        const date = new Date(timestamp * 60000);
        switch (chartPeriod) {
            case '1M':
            case '3M':
            case 'YTD':
            case '1Y':
            case '5Y':
            case 'MAX':
                return formatDate(date, false);
            default:
                return formatDate(date, true);
        }
    }
    return '';
};
const getDefaultTime = (selectedPeriod, initialTimestamp) => {
    switch (selectedPeriod) {
        case '1D':
            return formatDate(new Date(), false, false);
        case '1W':
            return 'in the last 7 days';
        case '1M':
            return 'in the last 30 days';
        case '3M':
            return 'in the last 90 days';
        case 'YTD':
            return 'Year to date';
        case '1Y':
            return 'in the last year';
        case '5Y':
            return 'in the last 5 years';
        case 'MAX':
            return initialTimestamp ? `since ${formatDate(new Date(initialTimestamp * 60000), false, false)}` : '';
        default:
            return '';
    }
};
const getPriceChangeInfo = (open, close, currency) => {
    if (typeof open === 'number' && typeof close === 'number') {
        const value = close - open;
        const percent = ((close - open) / open) * 100;
        return getPriceChangeViewValues(value, percent, currency);
    }
    return getPriceChangeViewValues();
};
export const formatForToday = `H:mm 'Today', d MMMM`;
export const formatForTodayWithoutTime = `'Today', d MMMM`;
export const formatWithTime = 'H:mm E, d MMMM';
export const formatWithoutTime = 'E, d MMMM';
export const getFormatStr = (date, addTime) => {
    if (isToday(date)) {
        return addTime ? formatForToday : formatForTodayWithoutTime;
    }
    return (addTime ? formatWithTime : formatWithoutTime) + (isSameYear(date, new Date()) ? '' : ' yyyy');
};
export const formatTimestampForCandle = (timestamp, timestampEnd) => {
    const timestampDate = new Date(timestamp);
    const timestampDateEnd = timestampEnd ? new Date(timestampEnd) : new Date();
    const addTime = isSameDay(timestampDate, timestampDateEnd);
    return `${format(timestampDate, getFormatStr(timestampDate, addTime))} - ${format(timestampDateEnd, getFormatStr(timestampDateEnd, addTime))}`;
};
const getCandleData = (bars, currency) => {
    if (bars?.length && bars?.[0] !== null) {
        let min;
        let max;
        for (const bar of bars) {
            if (bar) {
                if (min === undefined || min.priceLow > bar.priceLow) {
                    min = bar;
                }
                if (max === undefined || max.priceHigh <= bar.priceHigh) {
                    max = bar;
                }
            }
        }
        const greatestX = bars.length - 1;
        const minY = min.priceLow;
        const maxY = max.priceHigh;
        const diff = maxY - minY;
        const candleData = [];
        let minCandlePoint;
        let maxCandlePoint;
        bars.forEach((barData, i) => {
            if (barData) {
                const priceHighTop = (1 - (barData.priceHigh - minY) / diff) * ACTUAL_CANDLE_CHART_HEIGHT;
                const priceLowBottom = (1 - (barData.priceLow - minY) / diff) * ACTUAL_CANDLE_CHART_HEIGHT;
                const priceChange = barData.priceClose - barData.priceOpen;
                const priceTrend = getPriceTrend(priceChange);
                const priceChangePct = (priceChange / barData.priceOpen) * 100;
                const priceChangePctStr = `${Math.abs(priceChangePct).toFixed(2)}%`;
                const priceOpenCloseMax = priceTrend === -1 ? barData.priceOpen : barData.priceClose;
                const priceOpenCloseMin = priceTrend === -1 ? barData.priceClose : barData.priceOpen;
                const priceOpenCloseTop = (1 - (priceOpenCloseMax - minY) / diff) * ACTUAL_CANDLE_CHART_HEIGHT;
                const priceOpenCloseBottom = (1 - (priceOpenCloseMin - minY) / diff) * ACTUAL_CANDLE_CHART_HEIGHT;
                const dataPoint = {
                    originalX: i,
                    x: i / greatestX,
                    priceLowBottom,
                    priceHighLowTop: priceHighTop,
                    priceHighLowHeight: priceLowBottom - priceHighTop,
                    priceTrend,
                    priceOpenCloseTop,
                    priceOpenCloseHeight: priceOpenCloseBottom - priceOpenCloseTop || rem(1),
                    priceChangePct: priceChangePctStr,
                    priceChangeValue: `${priceChangePctStr} (${formatAmount(Math.abs(priceChange), currency, 2)})`,
                    priceLow: formatAmount(barData.priceLow, currency, 2),
                    priceHigh: formatAmount(barData.priceHigh, currency, 2),
                    priceOpen: formatAmount(barData.priceOpen, currency, 2),
                    priceClose: formatAmount(barData.priceClose, currency, 2),
                    priceCloseRaw: barData.priceClose,
                    timeStamp: barData.timestamp,
                    formattedTimeStamp: formatTimestampForCandle(barData.timestamp, barData.timestampEnd),
                };
                if (!minCandlePoint || minCandlePoint.priceLowBottom < dataPoint.priceLowBottom) {
                    minCandlePoint = dataPoint;
                }
                if (!maxCandlePoint || maxCandlePoint.priceHighLowTop >= dataPoint.priceHighLowTop) {
                    maxCandlePoint = dataPoint;
                }
                candleData.push(dataPoint);
            }
            else {
                candleData.push(null);
            }
        });
        return {
            candleData,
            minCandlePoint,
            maxCandlePoint,
        };
    }
    return {
        candleData: [],
        minCandlePoint: undefined,
        maxCandlePoint: undefined,
    };
};
export const getChartData = (response, chartPeriod, currency) => {
    const { timestamps, prevClose, closePrices, postTradingClosePrices, postTradingTimestamps } = response;
    const arrLen = closePrices.length;
    const notNullArr = closePrices.filter((price) => price !== null);
    /**
     * for rendering post trading prices in the graph
     * the conditions are:
     * 1. closePrices should be all numbers
     * 2. chartPeriod id 1D
     * 3. postTradingClosePrices should have length no matter it be an array of nulls
     *
     * the postTradingClosePrices is empty when the stock market is active
     */
    const hasPostTradingPrices = !!(arrLen === notNullArr.length &&
        chartPeriod === '1D' &&
        postTradingClosePrices?.length &&
        postTradingTimestamps?.length);
    const notNullPostTradingArr = (hasPostTradingPrices ? postTradingClosePrices?.filter((price) => price !== null) || [] : []);
    const pricesArr = hasPostTradingPrices ? [...closePrices, ...postTradingClosePrices] : closePrices;
    const timestampsArr = hasPostTradingPrices ? [...timestamps, ...postTradingTimestamps] : timestamps;
    const notNullFinalArr = hasPostTradingPrices ? [...notNullArr, ...notNullPostTradingArr] : notNullArr;
    const finalArrLen = pricesArr.length;
    if (notNullArr.length) {
        const isAllSame = pricesArr.every((value) => value === null || value === pricesArr[0]); // nulls don’t count
        // yRange is needed to pad the graph
        let yRange;
        const midIndex = Math.floor((finalArrLen - 1) / 2);
        const shouldUsePrevClose = chartPeriod === '1D' && prevClose;
        if (isAllSame) {
            if (pricesArr[0] === null) {
                // this is the case when the market has just started and there are no datapoints yet
                if (shouldUsePrevClose) {
                    yRange = [prevClose - 1, prevClose + 1];
                }
                else {
                    yRange = [-1, 1];
                }
            }
            else {
                const max = shouldUsePrevClose ? Math.max(pricesArr[0], prevClose) : pricesArr[0];
                const min = shouldUsePrevClose ? Math.min(pricesArr[0], prevClose) : pricesArr[0];
                yRange = [min - 1, max + 1];
            }
        }
        else if (pricesArr[midIndex] === null) {
            // Checking to see if the 1D graph is half empty and shift the graph to make it less jumpy
            const max = shouldUsePrevClose
                ? Math.max(...notNullFinalArr, notNullFinalArr[0] + 2, prevClose)
                : Math.max(...notNullFinalArr, notNullFinalArr[0] + 2);
            const min = shouldUsePrevClose
                ? Math.min(...notNullFinalArr, notNullFinalArr[0] - 2, prevClose)
                : Math.min(...notNullFinalArr, notNullFinalArr[0] - 2);
            yRange = [min, max];
        }
        else {
            const greatestY = shouldUsePrevClose ? Math.max(...notNullFinalArr, prevClose) : Math.max(...notNullFinalArr);
            const smallestY = shouldUsePrevClose ? Math.min(...notNullFinalArr, prevClose) : Math.min(...notNullFinalArr);
            yRange = [smallestY, greatestY];
        }
        // Addressing condition where there is only 1 point
        if (arrLen === 1) {
            if (pricesArr[0] === undefined) {
                pricesArr[0] = 0;
                pricesArr.push(0);
            }
            else {
                pricesArr.push(pricesArr[0]);
            }
        }
        const minY = yRange[0];
        const maxY = yRange[1];
        const smallestX = 0;
        const greatestX = finalArrLen - 1;
        const parsedOriginalData = [];
        let minPoint;
        let maxPoint;
        for (let i = 0; i < finalArrLen; i += 1) {
            const d = pricesArr[i];
            if (d !== null) {
                const dataPoint = {
                    originalX: i,
                    originalY: d,
                    x: (i - smallestX) / (greatestX - smallestX),
                    y: 1 - (d - minY) / (maxY - minY),
                    timeStamp: getFormattedTimeStamp(timestampsArr[i], chartPeriod),
                    timeStampRaw: timestampsArr[i],
                    ...getPriceChangeInfo(shouldUsePrevClose ? prevClose : pricesArr[0], d, currency),
                };
                if (!isAllSame) {
                    if (!minPoint || minPoint.originalY > dataPoint.originalY) {
                        minPoint = dataPoint;
                    }
                    if (!maxPoint || maxPoint.originalY <= dataPoint.originalY) {
                        maxPoint = dataPoint;
                    }
                }
                parsedOriginalData.push(dataPoint);
            }
        }
        const initialPoint = parsedOriginalData[0];
        const parsedOriginalDataLen = parsedOriginalData.length;
        const isMarketActive = finalArrLen !== notNullFinalArr.length;
        const isMarketLive = arrLen !== notNullArr.length;
        const shouldShowMaxMin = pricesArr[midIndex] !== null;
        const finalPoint = parsedOriginalDataLen ? parsedOriginalData[parsedOriginalDataLen - 1] : null;
        const priceChange = 
        // eslint-disable-next-line no-nested-ternary
        finalPoint !== null
            ? chartPeriod === '1D' && prevClose
                ? finalPoint.originalY - prevClose
                : finalPoint.originalY - initialPoint.originalY
            : undefined;
        const initialPointInGraph = shouldUsePrevClose
            ? {
                x: 0,
                originalX: 0,
                originalY: prevClose,
                y: 1 - (prevClose - minY) / (maxY - minY),
                formattedPrice: formatAmount(prevClose, currency, 2),
            }
            : {
                ...initialPoint,
                formattedPrice: formatAmount(initialPoint.originalY, currency, 2),
            };
        const maxPointInGraph = !shouldShowMaxMin || !maxPoint
            ? null
            : { ...maxPoint, formattedPrice: formatAmount(maxPoint.originalY, currency, 2) }; // used to show the max point in the graph
        const minPointInGraph = !shouldShowMaxMin || !minPoint
            ? null
            : { ...minPoint, formattedPrice: formatAmount(minPoint.originalY, currency, 2) }; // used to show the min point in the graph
        const centerAlign = (() => {
            if (maxPointInGraph && initialPointInGraph.y < maxPointInGraph.y) {
                // case where initialPoint is greater than maxPoint && maxPoint is really close to initialPoint
                if (maxPointInGraph.x < 0.3 && maxPointInGraph.y < 0.3) {
                    return true;
                }
            }
            else if (minPointInGraph && minPointInGraph.y < initialPointInGraph.y) {
                // case where minPoint is greater than initialPoint && minPoint is really close to initialPoint
                if (minPointInGraph.x < 0.3 && minPointInGraph.y > 0.7) {
                    return true;
                }
            }
            return false;
        })();
        initialPointInGraph.centerAlign = centerAlign;
        const sliceIndex = isMarketLive ? Math.max(notNullArr.length * 0.5, 1) : finalArrLen * 0.66;
        return {
            dataPoints: hasPostTradingPrices ? parsedOriginalData.slice(0, arrLen) : parsedOriginalData.slice(0, sliceIndex),
            dataPoints2: hasPostTradingPrices
                ? parsedOriginalData.slice(arrLen - 1)
                : parsedOriginalData.slice(sliceIndex - 1),
            hasPostTradingPrices,
            strokeColorKey: getColorKey(priceChange),
            finalPoint: isMarketActive ? finalPoint : null,
            isActiveInPostTrading: isMarketActive && hasPostTradingPrices,
            defaultTimeStamp: getDefaultTime(chartPeriod, timestamps[0]),
            initialPoint: initialPointInGraph,
            ...getPriceChangeInfo(initialPointInGraph.originalY, finalPoint?.originalY, currency),
            maxPoint: maxPointInGraph,
            minPoint: minPointInGraph,
            ...getCandleData(response.bars, currency),
        };
    }
    return {
        dataPoints: [],
        dataPoints2: [],
        finalPoint: null,
        strokeColorKey: undefined,
        hasPostTradingPrices: false,
        isActiveInPostTrading: false,
        defaultTimeStamp: chartPeriod === '1D' ? getDefaultTime(chartPeriod, null) : '',
        initialPoint: chartPeriod === '1D' && typeof prevClose === 'number'
            ? {
                x: 0,
                y: 0.5,
                originalX: 0,
                originalY: prevClose,
                centerAlign: false,
                formattedPrice: formatAmount(prevClose, currency, 2),
            }
            : null,
        ...getPriceChangeInfo(undefined, undefined, undefined),
        maxPoint: null,
        minPoint: null,
        ...getCandleData(response?.bars, currency),
    };
};
export const getPriceTrend = (priceChange) => {
    if (priceChange) {
        if (priceChange > 0) {
            return 1;
        }
        if (priceChange < 0) {
            return -1;
        }
        return 0;
    }
    return undefined;
};
export const getPriceChangeViewValues = (priceChange, priceChangePct, currency) => {
    if (priceChange !== undefined && priceChangePct !== undefined) {
        return {
            priceTrend: getPriceTrend(priceChange),
            priceChangeValue: `${Math.abs(priceChangePct).toFixed(2)}% (${formatAmount(Math.abs(priceChange), currency, 2)})`,
        };
    }
    return {
        priceTrend: getPriceTrend(),
        priceChangeValue: '',
    };
};
export const getColorKeyFromPriceTrendWorklet = (priceTrend) => {
    'worklet';
    if (priceTrend === -1) {
        return 'negative';
    }
    if (priceTrend === 1) {
        return 'positive';
    }
    return 'secondary';
};
export const getColorKeyFromPriceTrend = (priceTrend) => {
    if (priceTrend === -1) {
        return 'negative';
    }
    if (priceTrend === 1) {
        return 'positive';
    }
    return 'secondary';
};
export const getCandleColorFromPriceTrend = (priceTrend) => {
    if (priceTrend === -1) {
        return 'negative';
    }
    if (priceTrend === 1) {
        return 'positive';
    }
    return 'secondary';
};
